/**
 * Bloc tarification org (camping / refuge) — modes Simplifié · Détaillé · Désuet.
 * Source Local | Profil organisation ; Détaillé = message (grilles S5 panel).
 */

import type { PricingModeOption } from './locationFormPricingConstants'
import type { PricingProfile, PricingSource } from '../types'
import { useTranslation } from 'react-i18next'

interface LocationFormPricingSectionProps {
  showTabs: boolean
  activeTabIsPricing: boolean
  isCamping: boolean
  isRefuge: boolean
  isLoading: boolean
  pricingSource: PricingSource
  setPricingSource: (s: PricingSource) => void
  pricingProfileId: number | ''
  setPricingProfileId: (id: number | '') => void
  availableProfiles: PricingProfile[]
  organizationId?: number
  organizationEditHref?: string
  pricingMode: PricingModeOption
  setPricingMode: (m: PricingModeOption) => void
  pricingModeOptions: { value: PricingModeOption; label: string; hint: string }[]
  taxesIncluded: boolean
  setTaxesIncluded: (v: boolean) => void
  accessIncluded: boolean
  setAccessIncluded: (v: boolean) => void
  simplePriceMin: string
  setSimplePriceMin: (v: string) => void
  simplePriceMax: string
  setSimplePriceMax: (v: string) => void
  summerAvailable: boolean
  setSummerAvailable: (v: boolean) => void
  winterAvailable: boolean
  setWinterAvailable: (v: boolean) => void
  rentalType: string
  setRentalType: (v: string) => void
  rentalTypeOptions: readonly { value: string; label: string }[]
  showRefugeDormitoryPriceField: boolean
  dormitoryPrice: string
  setDormitoryPrice: (v: string) => void
  dormitoryPriceMax: string
  setDormitoryPriceMax: (v: string) => void
  showRefugeExclusivePriceField: boolean
  exclusivePrice: string
  setExclusivePrice: (v: string) => void
  exclusivePriceMax: string
  setExclusivePriceMax: (v: string) => void
  fee: string
  setFee: (v: string) => void
  feeOptions: readonly { value: string; label: string }[]
  priceMin: string
  setPriceMin: (v: string) => void
  summerPrice: string
  setSummerPrice: (v: string) => void
  winterPrice: string
  setWinterPrice: (v: string) => void
  priceDate: string
  setPriceDate: (v: string) => void
  cost: string
  setCost: (v: string) => void
  costOptions: readonly { value: string; label: string }[]
  showRefugeExclusiveOccupationField: boolean
  exclusiveOccupation: string
  setExclusiveOccupation: (v: string) => void
  exclusiveOccupationOptions: readonly { value: string; label: string }[]
  showRefugePriceDateField: boolean
  reservation: string
  setReservation: (v: string) => void
  reservationOptions: readonly { value: string; label: string }[]
  reservationUrl: string
  setReservationUrl: (v: string) => void
  reservationEmail: string
  setReservationEmail: (v: string) => void
  reservationPhone: string
  setReservationPhone: (v: string) => void
}

export function LocationFormPricingSection(props: LocationFormPricingSectionProps) {
  const { t } = useTranslation('organizations')
  const {
    showTabs,
    activeTabIsPricing,
    isCamping,
    isRefuge,
    isLoading,
    pricingSource,
    setPricingSource,
    pricingProfileId,
    setPricingProfileId,
    availableProfiles,
    organizationId,
    organizationEditHref,
    pricingMode,
    setPricingMode,
    pricingModeOptions,
  } = props

  const showContact =
    props.reservation === 'Obligatoire' || props.reservation === 'Possible'
  const fromProfile = pricingSource === 'profile'
  const pricingDisabled = isLoading || fromProfile
  const pricingProfilesHref =
    organizationEditHref ||
    (organizationId ? `/organizations/${organizationId}/edit#tarification` : null)

  return (
    <section
      className="location-form__section location-form__pane location-form__pricing"
      data-tab="pricing"
      id={showTabs ? 'location-form-panel-pricing' : undefined}
      role={showTabs ? 'tabpanel' : undefined}
      aria-labelledby={showTabs ? t('LocationFormPricingSection.location_form_tab_pricing_location') : 'location-form-pricing-title'}
      hidden={showTabs && !activeTabIsPricing}
    >
      <h3 id="location-form-pricing-title" className="location-form__section-title">
        {t('LocationFormPricingSection.tarification_reservation')}
      </h3>

      <fieldset className="location-form__pricing-modes">
        <legend className="location-form__label">{t('LocationFormPricingSection.source_tarification')}</legend>
        <div className="location-form__radio-cards" role="radiogroup" aria-label={t('LocationFormPricingSection.source_tarification')}>
          <label
            className={`location-form__radio-card${pricingSource === 'local' ? ' location-form__radio-card--active' : ''}`}
          >
            <input
              type="radio"
              name="pricing_source"
              value="local"
              checked={pricingSource === 'local'}
              onChange={() => setPricingSource('local')}
              disabled={isLoading}
            />
            <span className="location-form__radio-card-title">{t('LocationFormPricingSection.local')}</span>
            <span className="location-form__radio-card-hint">
              {t('LocationFormPricingSection.grille_definie_lieu')}
            </span>
          </label>
          <label
            className={`location-form__radio-card${pricingSource === 'profile' ? ' location-form__radio-card--active' : ''}`}
          >
            <input
              type="radio"
              name="pricing_source"
              value="profile"
              checked={pricingSource === 'profile'}
              onChange={() => setPricingSource('profile')}
              disabled={isLoading || availableProfiles.length === 0}
            />
            <span className="location-form__radio-card-title">{t('LocationFormPricingSection.profil_organisation')}</span>
            <span className="location-form__radio-card-hint">
              {t('LocationFormPricingSection.reference_live_profil_organisation')}
            </span>
          </label>
        </div>
        {availableProfiles.length === 0 && (
          <div className="location-form__pricing-empty-profiles">
            <p className="location-form__hint">
              {t('LocationFormPricingSection.aucun_profil_disponible_type_lieu')}{' '}
              <strong>{t('LocationFormPricingSection.local')}</strong> {t('LocationFormPricingSection.editez_prix_ici')}
            </p>
            {pricingProfilesHref && (
              <a
                className="location-form__btn location-form__btn--secondary location-form__btn--inline"
                href={pricingProfilesHref}
              >
                {t('LocationFormPricingSection.creer_profil_organisation')}
              </a>
            )}
          </div>
        )}
      </fieldset>

      {fromProfile && (
        <div className="location-form__pricing-block location-form__pricing-block--notice">
          <div className="location-form__field">
            <label htmlFor="location-form-pricing-profile" className="location-form__label">
              {t('LocationFormPricingSection.profil_tarifaire')}
            </label>
            <select
              id="location-form-pricing-profile"
              className="location-form__select"
              value={pricingProfileId === '' ? '' : String(pricingProfileId)}
              onChange={(e) => {
                const v = e.target.value
                setPricingProfileId(v ? parseInt(v, 10) : '')
              }}
              disabled={isLoading}
            >
              <option value="">{t('LocationFormPricingSection.choisir_profil')}</option>
              {availableProfiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.pricing_mode ? ` (${p.pricing_mode})` : ''}
                </option>
              ))}
            </select>
          </div>
          <p className="location-form__hint">
            {t('LocationFormPricingSection.prix_affiches_viennent_profil_organisati')}
          </p>
        </div>
      )}

      <fieldset className="location-form__pricing-modes">
        <legend className="location-form__label">{t('LocationFormPricingSection.mode_tarification')}</legend>
        <div className="location-form__radio-cards" role="radiogroup" aria-label={t('LocationFormPricingSection.mode_tarification')}>
          {pricingModeOptions.map((opt) => (
            <label
              key={opt.value}
              className={`location-form__radio-card${pricingMode === opt.value ? ' location-form__radio-card--active' : ''}`}
            >
              <input
                type="radio"
                name="pricing_mode"
                value={opt.value}
                checked={pricingMode === opt.value}
                onChange={() => setPricingMode(opt.value)}
                disabled={pricingDisabled}
              />
              <span className="location-form__radio-card-title">{opt.label}</span>
              <span className="location-form__radio-card-hint">{opt.hint}</span>
            </label>
          ))}
        </div>
        <p className="location-form__hint">
          {t('LocationFormPricingSection.simplifie_partir_entre_detaille_grilles')}
        </p>
      </fieldset>

      {pricingMode === 'free' && (
        <div className="location-form__pricing-block location-form__pricing-block--notice">
          <p className="location-form__hint" style={{ margin: 0 }}>
            {t('LocationFormPricingSection.lieu')} <strong>gratuit</strong>{t('LocationFormPricingSection.aucun_prix_ne_sera_affiche')}
          </p>
        </div>
      )}

      {(pricingMode === 'simple' || pricingMode === 'detailed') && (
        <div className="location-form__pricing-inclusions">
          <p className="location-form__subsection-title">{t('LocationFormPricingSection.inclusions')}</p>
          <div className="location-form__radio-cards location-form__radio-cards--compact" role="radiogroup" aria-label={t('LocationFormPricingSection.taxes')}>
            <span className="location-form__label">{t('LocationFormPricingSection.taxes')}</span>
            <label className={`location-form__radio-card${props.taxesIncluded ? ' location-form__radio-card--active' : ''}`}>
              <input
                type="radio"
                name="taxes_included"
                checked={props.taxesIncluded}
                onChange={() => props.setTaxesIncluded(true)}
                disabled={pricingDisabled}
              />
              {t('LocationFormPricingSection.taxe_incluse')}
            </label>
            <label className={`location-form__radio-card${!props.taxesIncluded ? ' location-form__radio-card--active' : ''}`}>
              <input
                type="radio"
                name="taxes_included"
                checked={!props.taxesIncluded}
                onChange={() => props.setTaxesIncluded(false)}
                disabled={pricingDisabled}
              />
              {t('LocationFormPricingSection.plus_taxe')}
            </label>
          </div>
          <div className="location-form__radio-cards location-form__radio-cards--compact" role="radiogroup" aria-label={t('LocationFormPricingSection.acces')}>
            <span className="location-form__label">{t('LocationFormPricingSection.acces')}</span>
            <label className={`location-form__radio-card${props.accessIncluded ? ' location-form__radio-card--active' : ''}`}>
              <input
                type="radio"
                name="access_included"
                checked={props.accessIncluded}
                onChange={() => props.setAccessIncluded(true)}
                disabled={pricingDisabled}
              />
              {t('LocationFormPricingSection.acces_inclus')}
            </label>
            <label className={`location-form__radio-card${!props.accessIncluded ? ' location-form__radio-card--active' : ''}`}>
              <input
                type="radio"
                name="access_included"
                checked={!props.accessIncluded}
                onChange={() => props.setAccessIncluded(false)}
                disabled={pricingDisabled}
              />
              {t('LocationFormPricingSection.acces_sus')}
            </label>
          </div>
        </div>
      )}

      {pricingMode === 'simple' && isCamping && (
        <div className="location-form__pricing-block">
          <p className="location-form__subsection-title">{t('LocationFormPricingSection.mode_simplifie')}</p>
          <div className="location-form__field-row">
            <div className="location-form__field">
              <label htmlFor="location-form-simple-min" className="location-form__label">
                {t('LocationFormPricingSection.prix_partir')}
              </label>
              <input
                id="location-form-simple-min"
                type="number"
                step="0.01"
                min={0}
                className="location-form__input"
                value={props.simplePriceMin}
                onChange={(e) => props.setSimplePriceMin(e.target.value)}
                disabled={pricingDisabled}
                readOnly={fromProfile}
              />
            </div>
            <div className="location-form__field">
              <label htmlFor="location-form-simple-max" className="location-form__label">
                {t('LocationFormPricingSection.prix_jusqu')}
              </label>
              <input
                id="location-form-simple-max"
                type="number"
                step="0.01"
                min={0}
                className="location-form__input"
                value={props.simplePriceMax}
                onChange={(e) => props.setSimplePriceMax(e.target.value)}
                disabled={pricingDisabled}
                readOnly={fromProfile}
              />
            </div>
          </div>
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.summerAvailable}
                onChange={(e) => props.setSummerAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_ete')}</span>
            </label>
          </div>
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.winterAvailable}
                onChange={(e) => props.setWinterAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_hiver')}</span>
            </label>
          </div>
        </div>
      )}

      {pricingMode === 'simple' && isRefuge && (
        <div className="location-form__pricing-block">
          <p className="location-form__subsection-title">{t('LocationFormPricingSection.mode_simplifie')}</p>
          <div className="location-form__field">
            <label htmlFor="location-form-rental-type" className="location-form__label">
              {t('LocationFormPricingSection.type_location')}
            </label>
            <select
              id="location-form-rental-type"
              className="location-form__select"
              value={props.rentalType}
              onChange={(e) => props.setRentalType(e.target.value)}
              disabled={isLoading}
            >
              {props.rentalTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field-row">
            {props.showRefugeDormitoryPriceField && (
              <div className="location-form__pricing-subcard">
                <p className="location-form__subsection-title">{t('LocationFormPricingSection.dortoir')}</p>
                <div className="location-form__field">
                  <label htmlFor="location-form-dormitory-price" className="location-form__label">
                    {t('LocationFormPricingSection.prix_min')}
                  </label>
                  <input
                    id="location-form-dormitory-price"
                    type="number"
                    step="0.01"
                    min={0}
                    className="location-form__input"
                    value={props.dormitoryPrice}
                    onChange={(e) => props.setDormitoryPrice(e.target.value)}
                    disabled={pricingDisabled}
                    readOnly={fromProfile}
                  />
                </div>
                <div className="location-form__field">
                  <label htmlFor="location-form-dormitory-price-max" className="location-form__label">
                    {t('LocationFormPricingSection.prix_max')}
                  </label>
                  <input
                    id="location-form-dormitory-price-max"
                    type="number"
                    step="0.01"
                    min={0}
                    className="location-form__input"
                    value={props.dormitoryPriceMax}
                    onChange={(e) => props.setDormitoryPriceMax(e.target.value)}
                    disabled={pricingDisabled}
                    readOnly={fromProfile}
                  />
                </div>
              </div>
            )}
            {props.showRefugeExclusivePriceField && (
              <div className="location-form__pricing-subcard">
                <p className="location-form__subsection-title">{t('LocationFormPricingSection.exclusif')}</p>
                <div className="location-form__field">
                  <label htmlFor="location-form-exclusive-price" className="location-form__label">
                    {t('LocationFormPricingSection.prix_min')}
                  </label>
                  <input
                    id="location-form-exclusive-price"
                    type="number"
                    step="0.01"
                    min={0}
                    className="location-form__input"
                    value={props.exclusivePrice}
                    onChange={(e) => props.setExclusivePrice(e.target.value)}
                    disabled={pricingDisabled}
                    readOnly={fromProfile}
                  />
                </div>
                <div className="location-form__field">
                  <label htmlFor="location-form-exclusive-price-max" className="location-form__label">
                    {t('LocationFormPricingSection.prix_max')}
                  </label>
                  <input
                    id="location-form-exclusive-price-max"
                    type="number"
                    step="0.01"
                    min={0}
                    className="location-form__input"
                    value={props.exclusivePriceMax}
                    onChange={(e) => props.setExclusivePriceMax(e.target.value)}
                    disabled={pricingDisabled}
                    readOnly={fromProfile}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.summerAvailable}
                onChange={(e) => props.setSummerAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_ete')}</span>
            </label>
          </div>
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.winterAvailable}
                onChange={(e) => props.setWinterAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_hiver')}</span>
            </label>
          </div>
        </div>
      )}

      {pricingMode === 'detailed' && (
        <div className="location-form__pricing-block location-form__pricing-block--notice">
          <p className="location-form__subsection-title">{t('LocationFormPricingSection.mode_detaille')}</p>
          <p className="location-form__hint">
            {fromProfile
              ? t('LocationFormPricingSection.grilles_s5_regles_s6_profil')
              : t('LocationFormPricingSection.grilles_s5_regles_s6_s')}
          </p>
        </div>
      )}

      {pricingMode === 'desuet' && isCamping && (
        <div className="location-form__pricing-block">
          <p className="location-form__subsection-title">{t('LocationFormPricingSection.mode_desuet_champs_legacy')}</p>
          <p className="location-form__hint">
            {t('LocationFormPricingSection.anciens_champs_fee_ete_hiver')}
          </p>
          <div className="location-form__field">
            <label htmlFor="location-form-fee" className="location-form__label">
              {t('LocationFormPricingSection.frais_utilisation')}
            </label>
            <select
              id="location-form-fee"
              className="location-form__select"
              value={props.fee}
              onChange={(e) => props.setFee(e.target.value)}
              disabled={pricingDisabled}
            >
              {props.feeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-price-min" className="location-form__label">
              {t('LocationFormPricingSection.prix_minimum')}
            </label>
            <input
              id="location-form-price-min"
              type="number"
              step="0.01"
              min={0}
              className="location-form__input"
              value={props.priceMin}
              onChange={(e) => props.setPriceMin(e.target.value)}
              disabled={pricingDisabled}
              readOnly={fromProfile}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-summer-price-camp" className="location-form__label">
              {t('LocationFormPricingSection.prix_ete')}
            </label>
            <input
              id="location-form-summer-price-camp"
              type="number"
              step="0.01"
              min={0}
              className="location-form__input"
              value={props.summerPrice}
              onChange={(e) => props.setSummerPrice(e.target.value)}
              disabled={pricingDisabled}
              readOnly={fromProfile}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-winter-price-camp" className="location-form__label">
              {t('LocationFormPricingSection.prix_hiver')}
            </label>
            <input
              id="location-form-winter-price-camp"
              type="number"
              step="0.01"
              min={0}
              className="location-form__input"
              value={props.winterPrice}
              onChange={(e) => props.setWinterPrice(e.target.value)}
              disabled={pricingDisabled}
              readOnly={fromProfile}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-price-date-camp" className="location-form__label">
              {t('LocationFormPricingSection.annee_prix')}
            </label>
            <input
              id="location-form-price-date-camp"
              type="number"
              min={1900}
              max={2100}
              className="location-form__input"
              value={props.priceDate}
              onChange={(e) => props.setPriceDate(e.target.value)}
              placeholder={t('LocationFormPricingSection.ex_2025')}
              disabled={pricingDisabled}
              readOnly={fromProfile}
            />
          </div>
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.summerAvailable}
                onChange={(e) => props.setSummerAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_ete')}</span>
            </label>
          </div>
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.winterAvailable}
                onChange={(e) => props.setWinterAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_hiver')}</span>
            </label>
          </div>
        </div>
      )}

      {pricingMode === 'desuet' && isRefuge && (
        <div className="location-form__pricing-block">
          <p className="location-form__subsection-title">{t('LocationFormPricingSection.mode_desuet_champs_legacy')}</p>
          <p className="location-form__hint">
            {t('LocationFormPricingSection.anciens_champs_cout_occupation_exclusive')}
          </p>
          <div className="location-form__field">
            <label htmlFor="location-form-rental-type-desuet" className="location-form__label">
              {t('LocationFormPricingSection.type_location')}
            </label>
            <select
              id="location-form-rental-type-desuet"
              className="location-form__select"
              value={props.rentalType}
              onChange={(e) => props.setRentalType(e.target.value)}
              disabled={isLoading}
            >
              {props.rentalTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          {props.showRefugeExclusiveOccupationField && (
            <div className="location-form__field">
              <label htmlFor="location-form-exclusive" className="location-form__label">
                {t('LocationFormPricingSection.occupation_exclusive_24h')}
              </label>
              <select
                id="location-form-exclusive"
                className="location-form__select"
                value={props.exclusiveOccupation}
                onChange={(e) => props.setExclusiveOccupation(e.target.value)}
                disabled={isLoading}
              >
                {props.exclusiveOccupationOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="location-form__field">
            <label htmlFor="location-form-cost" className="location-form__label">
              {t('LocationFormPricingSection.frais_utilisation')}
            </label>
            <select
              id="location-form-cost"
              className="location-form__select"
              value={props.cost}
              onChange={(e) => props.setCost(e.target.value)}
              disabled={pricingDisabled}
            >
              {props.costOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-simple-min-desuet" className="location-form__label">
              {t('LocationFormPricingSection.prix_partir_fourchette_globale')}
            </label>
            <input
              id="location-form-simple-min-desuet"
              type="number"
              step="0.01"
              min={0}
              className="location-form__input"
              value={props.simplePriceMin}
              onChange={(e) => props.setSimplePriceMin(e.target.value)}
              disabled={pricingDisabled}
              readOnly={fromProfile}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-simple-max-desuet" className="location-form__label">
              {t('LocationFormPricingSection.prix_jusqu')}
            </label>
            <input
              id="location-form-simple-max-desuet"
              type="number"
              step="0.01"
              min={0}
              className="location-form__input"
              value={props.simplePriceMax}
              onChange={(e) => props.setSimplePriceMax(e.target.value)}
              disabled={pricingDisabled}
              readOnly={fromProfile}
            />
          </div>
          {props.showRefugePriceDateField && (
            <div className="location-form__field">
              <label htmlFor="location-form-price-date" className="location-form__label">
                {t('LocationFormPricingSection.annee_prix')}
              </label>
              <input
                id="location-form-price-date"
                type="number"
                min={1900}
                max={2100}
                className="location-form__input"
                value={props.priceDate}
                onChange={(e) => props.setPriceDate(e.target.value)}
                placeholder={t('LocationFormPricingSection.ex_2025')}
                disabled={pricingDisabled}
                readOnly={fromProfile}
              />
            </div>
          )}
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.summerAvailable}
                onChange={(e) => props.setSummerAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_ete')}</span>
            </label>
          </div>
          <div className="location-form__field location-form__field--row">
            <label className="location-form__checkbox-wrap">
              <input
                type="checkbox"
                checked={props.winterAvailable}
                onChange={(e) => props.setWinterAvailable(e.target.checked)}
                disabled={isLoading}
              />
              <span>{t('LocationFormPricingSection.disponible_hiver')}</span>
            </label>
          </div>
        </div>
      )}

      <div className="location-form__pricing-block">
        <p className="location-form__subsection-title">{t('LocationFormPricingSection.reservation')}</p>
        <div className="location-form__field">
          <label htmlFor="location-form-reservation" className="location-form__label">
            {t('LocationFormPricingSection.reservation')}
          </label>
          <select
            id="location-form-reservation"
            className="location-form__select"
            value={props.reservation}
            onChange={(e) => props.setReservation(e.target.value)}
            disabled={isLoading}
          >
            {props.reservationOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {showContact && (
          <>
            <div className="location-form__field">
              <label htmlFor="location-form-reservation-url" className="location-form__label">
                {t('LocationFormPricingSection.url_reservation')}
              </label>
              <input
                id="location-form-reservation-url"
                type="url"
                className="location-form__input"
                value={props.reservationUrl}
                onChange={(e) => props.setReservationUrl(e.target.value)}
                placeholder="https://..."
                disabled={isLoading}
              />
            </div>
            <div className="location-form__field">
              <label htmlFor="location-form-reservation-email" className="location-form__label">
                {t('LocationFormPricingSection.email_reservation')}
              </label>
              <input
                id="location-form-reservation-email"
                type="email"
                className="location-form__input"
                value={props.reservationEmail}
                onChange={(e) => props.setReservationEmail(e.target.value)}
                placeholder="reservation@..."
                disabled={isLoading}
              />
            </div>
            <div className="location-form__field">
              <label htmlFor="location-form-reservation-phone" className="location-form__label">
                {t('LocationFormPricingSection.telephone_reservation')}
              </label>
              <input
                id="location-form-reservation-phone"
                type="tel"
                className="location-form__input"
                value={props.reservationPhone}
                onChange={(e) => props.setReservationPhone(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </>
        )}
      </div>
    </section>
  )
}
